import { useNavigate } from '@tanstack/react-router'
import { AlertTriangle, CheckCircle, Mail, XCircle } from 'lucide-react'
import React, { useState } from 'react'
import useEmailServiceStatusQuery from '../hooks/useEmailServiceStatusQuery'
import useNotifyAdminMutation from '../hooks/useNotifyAdminMutation'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { useAuthStore } from '@/features/auth/store/authStore'
import { UserRoleEnum } from '@/features/users/types/user'

const EmailServiceAlert: React.FC = () => {
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const [isNotifying, setIsNotifying] = useState(false)

  // Fetch email service status
  const {
    data: serviceStatus,
    isLoading,
    error: statusError,
  } = useEmailServiceStatusQuery()

  // Notify admin mutation
  const notifyAdminMutation = useNotifyAdminMutation(() => {
    setIsNotifying(false)
  })

  const handleNotifyAdmin = () => {
    if (!user) return

    setIsNotifying(true)
    notifyAdminMutation.mutate({
      message:
        'User reported that email service appears to be offline and requires reauthentication.',
      userEmail: user.email,
      userName: user.name,
    })
  }

  if (isLoading || !serviceStatus) return null

  if (serviceStatus.isOnline && !serviceStatus.reauthRequired) return null

  if (statusError) return null

  const isSystemAdmin = user?.roles.includes(UserRoleEnum.SYSTEM_ADMIN)

  return (
    <Card className="border-amber-200 bg-amber-50 mb-6">
      <CardHeader>
        <CardTitle className="flex items-center text-amber-800">
          <AlertTriangle className="h-5 w-5 mr-2" />
          Email Service Status
          <Badge
            variant="secondary"
            className="ml-2 bg-amber-200 text-amber-800"
          >
            {serviceStatus.reauthRequired
              ? 'Authentication Required'
              : 'Offline'}
          </Badge>
        </CardTitle>
        <CardDescription className="text-amber-700">
          {serviceStatus.reauthRequired
            ? 'Email service authentication has expired and needs to be renewed.'
            : 'Email service appears to be offline.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center text-sm text-amber-700">
              {serviceStatus.isOnline ? (
                <CheckCircle className="h-4 w-4 mr-1 text-green-600" />
              ) : (
                <XCircle className="h-4 w-4 mr-1 text-red-600" />
              )}
              Service: {serviceStatus.isOnline ? 'Online' : 'Offline'}
            </div>
            <div className="flex items-center text-sm text-amber-700">
              <Mail className="h-4 w-4 mr-1" />
              Last checked:{' '}
              {new Date(serviceStatus.lastChecked).toLocaleTimeString()}
            </div>
          </div>

          <div className="flex space-x-2">
            {isSystemAdmin ? (
              <Button
                variant="default"
                size="sm"
                onClick={() => navigate({ to: '/email-dashboard' })}
                className="bg-amber-600 hover:bg-amber-700"
              >
                <AlertTriangle className="h-4 w-4 mr-2" />
                Fix Authentication
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={handleNotifyAdmin}
                disabled={isNotifying || notifyAdminMutation.isPending}
                className="border-amber-300 text-amber-800 hover:bg-amber-100"
              >
                <Mail className="h-4 w-4 mr-2" />
                {isNotifying || notifyAdminMutation.isPending
                  ? 'Notifying...'
                  : 'Notify Admin'}
              </Button>
            )}
          </div>
        </div>

        {serviceStatus.reauthRequired && (
          <div className="mt-3 text-xs text-amber-600">
            <strong>Impact:</strong> Email notifications and receipts may not be
            sent until authentication is restored.
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default EmailServiceAlert
